import React from 'react';
import { Link } from 'react-router-dom';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { useTheme } from '../context/ThemeContext';
import { 
  FaArrowLeft as FaArrowLeftBase, 
  FaServer as FaServerBase, 
  FaDatabase as FaDatabaseBase, 
  FaReact as FaReactBase 
} from 'react-icons/fa';

// Fix for TS2786
const FaArrowLeft = FaArrowLeftBase as React.ElementType;
const FaServer = FaServerBase as React.ElementType;
const FaDatabase = FaDatabaseBase as React.ElementType;
const FaReact = FaReactBase as React.ElementType;

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const TicketingDashboard: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const colors = {
    primary: isDark ? '#6366f1' : '#3b82f6',
    success: isDark ? '#10b981' : '#10b981',
    warning: isDark ? '#f59e0b' : '#f59e0b',
    danger: isDark ? '#ef4444' : '#ef4444',
    text: isDark ? '#e5e7eb' : '#374151',
    grid: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
  };

  const barData = {
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [{
      label: 'Incoming Tickets',
      data: [12, 19, 15, 25, 22, 10, 8],
      backgroundColor: colors.primary,
      borderRadius: 4,
    }]
  };

  const pieData = {
    labels: ['Network', 'Hardware', 'Software', 'Access'],
    datasets: [{
      data: [30, 20, 35, 15],
      backgroundColor: [colors.danger, colors.warning, colors.primary, colors.success],
      borderWidth: 0,
    }]
  };

  return (
    <div className="min-h-screen bg-secondary pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <Link to="/portfolio" className="inline-flex items-center text-secondary hover:text-accent mb-8 transition-colors">
          <FaArrowLeft className="mr-2" /> Back to Portfolio
        </Link>

        <div className="grid lg:grid-cols-3 gap-12 mb-16">
          <div className="lg:col-span-2">
            <h1 className="text-4xl font-bold text-primary mb-4">IT Operations Command Center</h1>
            <p className="text-xl text-secondary mb-6">
              A centralized incident management dashboard designed to reduce Mean Time To Resolution (MTTR) by 35%.
            </p>
            <div className="flex gap-4 mb-8">
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium dark:bg-blue-900 dark:text-blue-200">Full Stack</span>
              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium dark:bg-green-900 dark:text-green-200">Real-time</span>
            </div>
          </div>
          
          <div className="bg-primary p-6 rounded-xl border border-primary shadow-sm">
            <h3 className="text-sm font-bold uppercase text-secondary mb-4 tracking-wider">Tech Stack</h3>
            <div className="space-y-3">
              <div className="flex items-center text-primary">
                <FaReact className="mr-3 text-blue-500" size={20} /> React & Redux
              </div>
              <div className="flex items-center text-primary">
                <FaServer className="mr-3 text-green-500" size={20} /> Node.js / Express
              </div>
              <div className="flex items-center text-primary">
                <FaDatabase className="mr-3 text-purple-500" size={20} /> PostgreSQL
              </div>
            </div>
          </div>
        </div>

        <div className="bg-primary rounded-xl shadow-2xl border border-primary overflow-hidden mb-16">
          <div className="bg-gray-900 px-4 py-2 flex items-center gap-2 border-b border-gray-800">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span className="ml-4 text-xs text-gray-400 font-mono">admin-dashboard.local/incidents</span>
          </div>
          
          <div className="p-6 lg:p-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              {[
                { label: 'Open Incidents', val: '42', color: 'text-red-500' },
                { label: 'Avg Response', val: '12m', color: 'text-blue-500' },
                { label: 'Resolved Today', val: '18', color: 'text-green-500' },
                { label: 'SLA Breach', val: '2', color: 'text-yellow-500' },
              ].map((kpi, i) => (
                <div key={i} className="bg-secondary p-4 rounded-lg border border-primary">
                  <p className="text-sm text-secondary mb-1">{kpi.label}</p>
                  <p className={`text-3xl font-bold ${kpi.color}`}>{kpi.val}</p>
                </div>
              ))}
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="md:col-span-2 bg-secondary p-4 rounded-lg border border-primary">
                <h4 className="text-primary font-semibold mb-4">Weekly Incident Volume</h4>
                <Bar data={barData} options={{ responsive: true, scales: { y: { grid: { color: colors.grid } }, x: { grid: { display: false } } } }} />
              </div>
              <div className="bg-secondary p-4 rounded-lg border border-primary">
                <h4 className="text-primary font-semibold mb-4">Category Distribution</h4>
                <Doughnut data={pieData} options={{ responsive: true, cutout: '70%' }} />
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <h2 className="text-2xl font-bold text-primary mb-4">The Challenge</h2>
            <p className="text-secondary leading-relaxed mb-6">
              The IT support team was struggling with fragmented data across email, Slack, and legacy tools. 
              Critical incidents were being missed, and reporting on team performance took hours of manual Excel work.
            </p>
            <h2 className="text-2xl font-bold text-primary mb-4">The Solution</h2>
            <p className="text-secondary leading-relaxed">
              I architected a Single Page Application (SPA) that aggregates data streams into a unified view. 
              Using WebSockets, the dashboard updates instantly when a new ticket is created, triggering visual alerts for high-priority items.
            </p>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-primary mb-4">Key Features</h2>
            <ul className="space-y-3 text-secondary">
              <li className="flex items-start">
                <span className="text-accent mr-2">•</span>
                Real-time WebSocket integration for sub-second updates.
              </li>
              <li className="flex items-start">
                <span className="text-accent mr-2">•</span>
                Role-based access control (RBAC) for Admins vs. Agents.
              </li>
              <li className="flex items-start">
                <span className="text-accent mr-2">•</span>
                Automated SLA breach countdown timers.
              </li>
              <li className="flex items-start">
                <span className="text-accent mr-2">•</span>
                Exportable PDF reports generated via Node.js streams.
              </li>
            </ul>
          </div>
        </div>

      </div>
    </div>
  );
};

export default TicketingDashboard;